import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { environment } from '@fitness-force/environments';
import {
  CreateMilestoneConfigRequest,
  UpdateMilestoneConfigRequest,
  GetMilestoneConfigResponse,
  PagedMilestoneConfigResponse,
  ListMilestoneConfigParams,
  ForceRecalculateRequest,
  ForceRecalculateResponse
} from '../models/milestone.models';
import { AppErrors } from '@fitness-force/models';

@Injectable({ providedIn: 'root' })
export class MilestoneConfigService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.BASE_URL;

  listMilestoneConfigurations(params?: ListMilestoneConfigParams): Observable<PagedMilestoneConfigResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.LIST;
    
    let httpParams = new HttpParams();
    if (params?.pageNumber !== undefined) {
      httpParams = httpParams.set('pageNumber', params.pageNumber.toString());
    }
    if (params?.pageSize !== undefined) {
      httpParams = httpParams.set('pageSize', params.pageSize.toString());
    }
    if (params?.isActive !== undefined) {
      httpParams = httpParams.set('isActive', params.isActive.toString());
    }

    return this.http.get<PagedMilestoneConfigResponse>(url, { params: httpParams });
  }

  getMilestoneConfiguration(configId: string | number): Observable<GetMilestoneConfigResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.GET_BY_ID
      .replace('{configId}', String(configId));
    
    return this.http.get<GetMilestoneConfigResponse>(url).pipe(
      map((response: GetMilestoneConfigResponse) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createMilestoneConfiguration(request: CreateMilestoneConfigRequest): Observable<GetMilestoneConfigResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.CREATE;
    
    return this.http.post<GetMilestoneConfigResponse>(url, request).pipe(
      map((response: GetMilestoneConfigResponse) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  } 

  updateMilestoneConfiguration(configId: string | number, request: UpdateMilestoneConfigRequest): Observable<GetMilestoneConfigResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.UPDATE
      .replace('{configId}', String(configId));
    
    return this.http.put<GetMilestoneConfigResponse>(url, request).pipe(
      map((response: GetMilestoneConfigResponse) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  activateMilestoneConfiguration(configId: string | number): Observable<void> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.ACTIVATE
      .replace('{configId}', String(configId));
    
    return this.http.patch<void>(url, {}).pipe(
      map((response: void) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  deactivateMilestoneConfiguration(configId: string | number): Observable<void> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.DEACTIVATE
      .replace('{configId}', String(configId));
    
    return this.http.patch<void>(url, {}).pipe(
      map((response: void) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  forceRecalculate(configId: string | number, request: ForceRecalculateRequest = {}): Observable<ForceRecalculateResponse> {
    const url = this.baseUrl + CONST_URL_SEGEMENTS.MILESTONE_CONFIGURATIONS.FORCE_RECALCULATE
      .replace('{configId}', String(configId));
    
    return this.http.post<ForceRecalculateResponse>(url, request).pipe(
      map((response: ForceRecalculateResponse) => {
        return response;
      }),
      catchError((error:any) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
